import React from 'react';
import PropTypes from 'prop-types';
import './GridsAndLayouts.scss';

const GUTTER_TOKEN_TO_PX = {
  condensed: 16,
  default: 16,
  spacious: 32,
};

/** @param {number|string} gutter */
function resolveGutterPx(gutter) {
  if (typeof gutter === 'string' && GUTTER_TOKEN_TO_PX[gutter] != null) {
    return GUTTER_TOKEN_TO_PX[gutter];
  }
  if (gutter === 48) {
    return 32;
  }
  return gutter;
}

// Container
const Container = ({ fluid = false, maxWidth, children, className = '', ...props }) => {
  const classNames = [
    'ds-container',
    fluid ? 'ds-container--fluid' : '',
    maxWidth && `ds-container--${maxWidth}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};

Container.propTypes = {
  children: PropTypes.node,
  fluid: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'dashboard', 'dashboard-central', 'desktop-grid']),
  className: PropTypes.string,
};

Container.defaultProps = {
  children: undefined,
  fluid: false,
  maxWidth: undefined,
  className: '',
};

// Row
const Row = ({ gutter = 'default', noGutters = false, children, className = '', ...props }) => {
  const gutterPx = resolveGutterPx(gutter);
  const classNames = [
    'ds-row',
    noGutters ? 'ds-row--no-gutters' : '',
    !noGutters && gutterPx ? `ds-row--gutter-${gutterPx}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};

Row.propTypes = {
  children: PropTypes.node,
  gutter: PropTypes.oneOfType([
    PropTypes.oneOf(['condensed', 'default', 'spacious']),
    PropTypes.oneOf([16, 24, 32, 48]),
  ]),
  noGutters: PropTypes.bool,
  className: PropTypes.string,
};

Row.defaultProps = {
  children: undefined,
  gutter: 'default',
  noGutters: false,
  className: '',
};

const FLEX_ALIGN_CLASS = {
  stretch: 'ds-flex-row--align-stretch',
  start: 'ds-flex-row--align-start',
  center: 'ds-flex-row--align-center',
  end: 'ds-flex-row--align-end',
  baseline: 'ds-flex-row--align-baseline',
};

const FLEX_JUSTIFY_CLASS = {
  start: 'ds-flex-row--justify-start',
  center: 'ds-flex-row--justify-center',
  end: 'ds-flex-row--justify-end',
  between: 'ds-flex-row--justify-between',
  around: 'ds-flex-row--justify-around',
  evenly: 'ds-flex-row--justify-evenly',
};

/** Horizontal flex row (not the 12-col grid `Row`). Use for toolbars, tab rows, inline clusters. */
const FlexRow = React.forwardRef(
  (
    {
      children,
      align = 'stretch',
      justify = 'start',
      wrap = false,
      gap,
      inline = false,
      fullWidth = true,
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const mergedStyle = { ...(style || {}) };
    if (gap != null) {
      mergedStyle.gap = typeof gap === 'number' ? `${gap}px` : gap;
    }
    const hasStyle = Object.keys(mergedStyle).length > 0;

    const classNames = [
      'ds-flex-row',
      inline && 'ds-flex-row--inline',
      fullWidth && !inline && 'ds-flex-row--full',
      wrap && 'ds-flex-row--wrap',
      FLEX_ALIGN_CLASS[align],
      FLEX_JUSTIFY_CLASS[justify],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} style={hasStyle ? mergedStyle : undefined} {...props}>
        {children}
      </div>
    );
  }
);

FlexRow.displayName = 'FlexRow';

FlexRow.propTypes = {
  children: PropTypes.node,
  align: PropTypes.oneOf(['stretch', 'start', 'center', 'end', 'baseline']),
  justify: PropTypes.oneOf(['start', 'center', 'end', 'between', 'around', 'evenly']),
  wrap: PropTypes.bool,
  /** Gap in px when number, or any CSS length when string (e.g. `'12px'`, `'1rem'`). */
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inline: PropTypes.bool,
  /** When false, row is `width: auto` (unless `inline` is true, which already shrinks). */
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.shape({}),
};

FlexRow.defaultProps = {
  children: undefined,
  align: 'stretch',
  justify: 'start',
  wrap: false,
  gap: undefined,
  inline: false,
  fullWidth: true,
  className: '',
  style: undefined,
};

const FLEX_COL_ALIGN_CLASS = {
  stretch: 'ds-flex-col--align-stretch',
  start: 'ds-flex-col--align-start',
  center: 'ds-flex-col--align-center',
  end: 'ds-flex-col--align-end',
  baseline: 'ds-flex-col--align-baseline',
};

const FLEX_COL_JUSTIFY_CLASS = {
  start: 'ds-flex-col--justify-start',
  center: 'ds-flex-col--justify-center',
  end: 'ds-flex-col--justify-end',
  between: 'ds-flex-col--justify-between',
  around: 'ds-flex-col--justify-around',
  evenly: 'ds-flex-col--justify-evenly',
};

/** Vertical flex column (not the grid `Col`). Use for badges, stacks, column regions. */
const FlexColumn = React.forwardRef(
  (
    {
      children,
      align = 'stretch',
      justify = 'start',
      wrap = false,
      gap,
      inline = false,
      fullWidth = true,
      fullHeight = false,
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const mergedStyle = { ...(style || {}) };
    if (gap != null) {
      mergedStyle.gap = typeof gap === 'number' ? `${gap}px` : gap;
    }
    const hasStyle = Object.keys(mergedStyle).length > 0;

    const classNames = [
      'ds-flex-col',
      inline && 'ds-flex-col--inline',
      fullWidth && !inline && 'ds-flex-col--full',
      fullHeight && 'ds-flex-col--full-height',
      wrap && 'ds-flex-col--wrap',
      FLEX_COL_ALIGN_CLASS[align],
      FLEX_COL_JUSTIFY_CLASS[justify],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} style={hasStyle ? mergedStyle : undefined} {...props}>
        {children}
      </div>
    );
  }
);

FlexColumn.displayName = 'FlexColumn';

FlexColumn.propTypes = {
  children: PropTypes.node,
  align: PropTypes.oneOf(['stretch', 'start', 'center', 'end', 'baseline']),
  justify: PropTypes.oneOf(['start', 'center', 'end', 'between', 'around', 'evenly']),
  wrap: PropTypes.bool,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inline: PropTypes.bool,
  fullWidth: PropTypes.bool,
  fullHeight: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.shape({}),
};

FlexColumn.defaultProps = {
  children: undefined,
  align: 'stretch',
  justify: 'start',
  wrap: false,
  gap: undefined,
  inline: false,
  fullWidth: true,
  fullHeight: false,
  className: '',
  style: undefined,
};

// Col
const Col = ({ xs, sm, md, lg, xl, auto = false, children, className = '', ...props }) => {
  const classNames = [
    'ds-col',
    auto && 'ds-col--auto',
    xs && `ds-col-xs-${xs}`,
    sm && `ds-col-sm-${sm}`,
    md && `ds-col-md-${md}`,
    lg && `ds-col-lg-${lg}`,
    xl && `ds-col-xl-${xl}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};

Col.propTypes = {
  children: PropTypes.node,
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  auto: PropTypes.bool,
  className: PropTypes.string,
};

Col.defaultProps = {
  children: undefined,
  xs: undefined,
  sm: undefined,
  md: undefined,
  lg: undefined,
  xl: undefined,
  auto: false,
  className: '',
};

// Spacer
const Spacer = ({ size, axis = 'both', className = '', ...props }) => {
  const classNames = ['ds-spacer', `ds-spacer-${size}`, axis && `ds-spacer--${axis}`, className]
    .filter(Boolean)
    .join(' ');

  return <div className={classNames} {...props} />;
};

Spacer.propTypes = {
  size: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]).isRequired,
  axis: PropTypes.oneOf(['x', 'y', 'both']),
  className: PropTypes.string,
};

Spacer.defaultProps = {
  axis: 'both',
  className: '',
};

/**
 * Full-width dashboard shell: fixed 240px sidebar, gap + main trailing from CSS vars (defaults in SCSS).
 */
const DashboardLayout = ({
  sidebar,
  children,
  stackOnSmall = true,
  className = '',
  sidebarClassName = '',
  mainClassName = '',
  ...props
}) => {
  const rootClass = ['ds-dashboard-layout', stackOnSmall && 'ds-dashboard-layout--stack-sm', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} {...props}>
      <aside className={['ds-dashboard-layout__sidebar', sidebarClassName].filter(Boolean).join(' ')}>{sidebar}</aside>
      <div className="ds-dashboard-layout__gap" aria-hidden />
      <main className={['ds-dashboard-layout__main', mainClassName].filter(Boolean).join(' ')}>{children}</main>
    </div>
  );
};

DashboardLayout.propTypes = {
  sidebar: PropTypes.node,
  children: PropTypes.node,
  /** When true, sidebar stacks above main below 768px. */
  stackOnSmall: PropTypes.bool,
  className: PropTypes.string,
  sidebarClassName: PropTypes.string,
  mainClassName: PropTypes.string,
};

DashboardLayout.defaultProps = {
  sidebar: null,
  children: undefined,
  stackOnSmall: true,
  className: '',
  sidebarClassName: '',
  mainClassName: '',
};

const GridsAndLayouts = {
  Container,
  Row,
  Col,
  FlexRow,
  FlexColumn,
  Spacer,
  DashboardLayout,
};

/** @deprecated Use `GridsAndLayouts` — same object, kept for backward compatibility */
const Grid = GridsAndLayouts;

export { Container, Row, Col, FlexRow, FlexColumn, Spacer, DashboardLayout, Grid };
export default GridsAndLayouts;
